/**
 * SleekHome - Modern, premium "Sleek" variant of the Radio/home live page.
 * Immersive hero player on top; scroll down reveals the Discover panel
 * (Artist of the Week + Genre Hubs + New Releases). Classic view is untouched.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Play, Pause, ChevronLeft, ChevronRight, ChevronDown, Users, Radio,
  Sparkles, Share2, ArrowRight, Loader2, Flame, X,
} from 'lucide-react';
import { toast } from 'sonner';
import TopNav from './TopNav';
import EnergyVisualizer from './EnergyVisualizer';
import VotingBar from './VotingBar';
import PromoKitModal from './PromoKitModal';
import { cn } from '../utils/helpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SleekHome = ({
  genres = [],
  selectedGenre,
  setSelectedGenre,
  navHeight = 0,
  user,
  isAuthenticated,
  rooms = [],
  currentRoomIndex = 0,
  setCurrentRoomIndex,
  currentTrack,
  isPlaying,
  isSnippetMode = false,
  energyLevel = 50,
  onPlayRoom,
  onPauseRoom,
  isRoomPlaying,
  playRoomTrack,
  onViewStory,
}) => {
  const navigate = useNavigate();
  const room = rooms[currentRoomIndex];
  const scrollRef = useRef(null);
  const discoverRef = useRef(null);

  const [hub, setHub] = useState(null);
  const [hubLoading, setHubLoading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [showVote, setShowVote] = useState(false);
  const [showPromo, setShowPromo] = useState(false);

  const hasTrack = !!currentTrack?.id;
  const handleTrackShare = async () => {
    if (hasTrack) { setShowPromo(true); return; }
    const url = window.location.origin + (room ? `/room/${room.id}` : '/');
    if (navigator.share) {
      try { await navigator.share({ title: 'NLT Radio', url }); return; }
      catch (e) { if (e && e.name === 'AbortError') return; }
    }
    try { await navigator.clipboard.writeText(url); toast.success('Link copied'); } catch (e) {}
  };

  // Resolve which genre hub to surface in Discover
  const genreSlug = selectedGenre && selectedGenre !== 'all'
    ? selectedGenre
    : (room?.genre && genres.some(g => g.id === room.genre) ? room.genre : 'hip-hop');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setHubLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/genres/${genreSlug}/hub`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok && !cancelled) setHub(await res.json());
      } catch (e) {
        console.error('sleek hub load error', e);
      } finally {
        if (!cancelled) setHubLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [genreSlug]);

  const goArtist = (dir) => {
    if (!rooms.length) return;
    let idx = currentRoomIndex + (dir === 'next' ? 1 : -1);
    if (idx < 0) idx = rooms.length - 1;
    if (idx >= rooms.length) idx = 0;
    setCurrentRoomIndex(idx);
    if (currentTrack && playRoomTrack) playRoomTrack(rooms[idx]);
  };

  const scrollToDiscover = () => {
    discoverRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const playing = room ? isRoomPlaying(room) : false;
  const thisRoomPlaying = currentTrack && room && (
    currentTrack.artist_id === room.id || currentTrack.uploader_id === room.id
  );
  const artwork = (thisRoomPlaying && currentTrack?.artwork) || room?.artistImage;

  const openArtist = () => {
    const uid = room?.artistUserId;
    if (!uid || uid.startsWith('admin_uploaded')) return;
    navigate(`/profile/${uid}`);
  };

  const spotlight = hub?.spotlight;
  const shareSpotlight = useCallback(async () => {
    if (sharing) return;
    setSharing(true);
    try {
      const res = await fetch(`${API_URL}/api/genres/${genreSlug}/spotlight-card`);
      if (!res.ok) throw new Error('card fetch failed');
      const blob = await res.blob();
      const fname = `nltradio_artist_of_the_week_${genreSlug}.jpg`;
      const file = new File([blob], fname, { type: 'image/jpeg' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try { await navigator.share({ files: [file], title: 'Artist of the Week — NLT Radio' }); return; }
        catch (err) { if (err && err.name === 'AbortError') return; }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 4000);
    } catch (e) {
      console.error('spotlight share error', e);
    } finally {
      setSharing(false);
    }
  }, [genreSlug, sharing]);

  const accent = genres.find(g => g.id === genreSlug)?.color || '#00F0FF';

  return (
    <div
      className="fixed inset-0 bg-[#050507] select-none"
      data-testid="sleek-home"
    >
      {/* Grain overlay */}
      <div
        className="pointer-events-none fixed inset-0 z-40 opacity-[0.04]"
        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")` }}
      />

      <TopNav
        genres={genres}
        selectedGenre={selectedGenre}
        onGenreChange={setSelectedGenre}
        user={user}
        isAuthenticated={isAuthenticated}
      />

      {/* Scroll surface: player screen -> discover screen */}
      <div
        ref={scrollRef}
        className="absolute inset-0 overflow-y-auto snap-y snap-mandatory scrollbar-hide overscroll-contain"
        data-testid="sleek-scroll-surface"
      >
        {/* ===== SCREEN 1 — Immersive hero player ===== */}
        <section
          className="relative h-[100dvh] w-full snap-start flex flex-col items-center justify-center px-6"
          style={{ paddingTop: (navHeight ? navHeight + 60 : 190), paddingBottom: 120 }}
          data-testid="sleek-hero-player"
        >
          {/* accent radial glow */}
          <div
            className="pointer-events-none absolute inset-0 transition-opacity duration-1000"
            style={{ background: `radial-gradient(60% 50% at 50% 42%, ${accent}22 0%, transparent 70%)` }}
          />

          {/* Artist prev / next */}
          {rooms.length > 1 && (
            <>
              <button
                onClick={() => goArtist('prev')}
                data-testid="sleek-artist-prev"
                className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/5 backdrop-blur-md border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => goArtist('next')}
                data-testid="sleek-artist-next"
                className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/5 backdrop-blur-md border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}

          {/* Vinyl deck */}
          <div className="relative z-10 flex flex-col items-center">
            <div className="relative" style={{ width: 256, height: 256 }}>
              <div className="absolute inset-0 flex items-center justify-center">
                <EnergyVisualizer energyLevel={energyLevel} isPlaying={playing} isLive={room?.isLive} />
              </div>

              {/* Spinning record */}
              <motion.div
                key={room?.id}
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.45, ease: 'easeOut' }}
                className="absolute inset-0 flex items-center justify-center"
              >
                <div
                  className={cn(
                    'relative w-[210px] h-[210px] rounded-full flex items-center justify-center',
                    'bg-[conic-gradient(from_0deg,#0b0b0f,#17171d,#0b0b0f,#17171d,#0b0b0f)]',
                    'shadow-[0_20px_60px_rgba(0,0,0,0.6)]',
                    'animate-[spin_16s_linear_infinite]',
                    !playing && '[animation-play-state:paused]'
                  )}
                  style={{ border: `1px solid ${accent}33` }}
                >
                  {/* vinyl grooves */}
                  <div className="absolute inset-3 rounded-full border border-white/5" />
                  <div className="absolute inset-8 rounded-full border border-white/5" />
                  <div className="absolute inset-14 rounded-full border border-white/5" />
                  {/* label / cover */}
                  <button
                    onClick={openArtist}
                    data-testid="sleek-artist-cover"
                    className="relative w-[104px] h-[104px] rounded-full overflow-hidden ring-2 ring-white/20"
                    style={{ boxShadow: `0 0 30px ${accent}55` }}
                  >
                    {artwork ? (
                      <img src={artwork} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#00F0FF] to-[#FF007F] flex items-center justify-center text-3xl font-black text-white">
                        {room?.artistName?.charAt(0) || '?'}
                      </div>
                    )}
                    <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[#050507] ring-2 ring-white/30" />
                  </button>
                </div>
              </motion.div>

              {/* Play / pause */}
              <motion.button
                whileTap={{ scale: 0.88 }}
                onClick={() => (playing ? onPauseRoom() : onPlayRoom(room))}
                data-testid="sleek-play-btn"
                className="absolute bottom-1 right-1 z-30 w-14 h-14 rounded-full flex items-center justify-center text-white shadow-[0_8px_30px_rgba(0,0,0,0.5)]"
                style={{ background: `linear-gradient(135deg, ${accent}, #FF007F)` }}
              >
                {playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-1" />}
              </motion.button>

              {room?.isLive && (
                <div className="absolute -top-1 left-1/2 -translate-x-1/2 z-30 px-3 py-1 rounded-full bg-[#FF007F] flex items-center gap-1.5 shadow-lg">
                  <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                  <span className="text-white text-[11px] font-bold tracking-wide">LIVE</span>
                </div>
              )}
            </div>

            {/* Track / artist */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="mt-6 text-center max-w-[86vw]"
            >
              <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-white/40 mb-1">
                {thisRoomPlaying ? 'Now Playing' : 'On NLT Radio'}
              </p>
              <h1
                className="text-3xl sm:text-4xl font-black text-white leading-none truncate"
                style={{ fontFamily: 'Unbounded, sans-serif' }}
              >
                {thisRoomPlaying ? currentTrack.track_title : (room?.artistName || 'NLT Radio')}
              </h1>
              <button
                onClick={openArtist}
                data-testid="sleek-artist-name"
                className="mt-1.5 text-base font-semibold truncate transition-colors hover:opacity-80"
                style={{ color: accent }}
              >
                {room?.artistName || 'Live Rooms'}
              </button>
            </motion.div>

            {/* Cleaned-up live reactions strip (replaces chaotic chat bubbles) */}
            <div className="mt-4 flex items-center gap-5">
              <div className="flex items-center gap-2 text-white/60">
                <Users className="w-4 h-4" />
                <span className="text-sm font-semibold">
                  {(room?.listenerCount || 0).toLocaleString()}
                </span>
              </div>
              <div className="flex items-end gap-[3px] h-5" aria-hidden>
                {[0.5, 0.8, 0.35, 0.95, 0.6, 0.75, 0.45].map((h, i) => (
                  <motion.span
                    key={i}
                    className="w-[3px] rounded-full"
                    style={{ background: accent }}
                    animate={{ height: playing ? [`${h * 40}%`, '100%', `${h * 50}%`] : '30%' }}
                    transition={{ duration: 0.9 + i * 0.12, repeat: Infinity, ease: 'easeInOut' }}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2 text-white/60">
                <Radio className="w-4 h-4" />
                <span className="text-sm font-semibold">{room?.tracks?.length || 0}</span>
              </div>
            </div>

            {/* Vote + Share (same actions as the Classic live page) */}
            <div className="mt-4 flex items-center gap-3">
              <button
                onClick={() => setShowVote(true)}
                data-testid="sleek-vote-btn"
                className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/5 border border-white/10 text-white/85 text-sm font-semibold hover:bg-white/10 transition-colors"
              >
                <Flame className="w-4 h-4 text-[#FF7A00]" /> Vote
              </button>
              <button
                onClick={handleTrackShare}
                data-testid="sleek-share-btn"
                className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/5 border border-white/10 text-white/85 text-sm font-semibold hover:bg-white/10 transition-colors"
              >
                <Share2 className="w-4 h-4" /> Share
              </button>
            </div>
          </div>

          {/* Scroll-for-discover cue */}
          <motion.button
            onClick={scrollToDiscover}
            data-testid="sleek-scroll-cue"
            className="absolute bottom-20 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-1 text-white/50 hover:text-white transition-colors"
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 1.8, repeat: Infinity }}
          >
            <span className="text-[11px] font-mono uppercase tracking-[0.2em] flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-[#FFD700]" /> Artist of the Week
            </span>
            <ChevronDown className="w-5 h-5" />
          </motion.button>
        </section>

        {/* ===== SCREEN 2 — Discover ===== */}
        <section
          ref={discoverRef}
          className="relative min-h-[100dvh] w-full snap-start px-5 pb-32 max-w-2xl mx-auto"
          style={{ paddingTop: (navHeight ? navHeight + 56 : 180) }}
          data-testid="discover-panel"
        >
          {/* Artist of the Week */}
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-[#FFD700]" />
            <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Artist of the Week
            </h2>
          </div>

          {hubLoading && !hub ? (
            <div className="h-40 rounded-2xl border border-white/10 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-[#FFD700] animate-spin" />
            </div>
          ) : spotlight ? (
            <div
              className="relative overflow-hidden rounded-2xl border border-[#FFD700]/25 bg-gradient-to-br from-[#FFD700]/10 via-[#FF007F]/10 to-transparent p-4"
              data-testid="sleek-aotw-card"
            >
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 rounded-2xl overflow-hidden bg-white/10 flex-shrink-0 ring-2 ring-[#FFD700]/40">
                  {(spotlight.artist_profile_picture || spotlight.top_track?.artwork) ? (
                    <img src={spotlight.artist_profile_picture || spotlight.top_track?.artwork} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/40 text-3xl font-black">
                      {(spotlight.artist_name || '?')[0]}
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[#FFD700] text-[11px] font-mono uppercase tracking-[0.2em]">{hub.label} Station</p>
                  <h3 className="text-white text-2xl font-black truncate leading-tight" data-testid="sleek-aotw-name">
                    {spotlight.artist_name || 'Featured Artist'}
                  </h3>
                  <p className="text-white/50 text-xs mt-0.5">
                    {spotlight.track_count} track{spotlight.track_count === 1 ? '' : 's'} · {spotlight.plays} play{spotlight.plays === 1 ? '' : 's'}
                  </p>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2">
                {spotlight.top_track && (
                  <button
                    onClick={() => playRoomTrack && playRoomTrack({ id: spotlight.artist_id, tracks: [spotlight.top_track] })}
                    data-testid="sleek-aotw-play"
                    className="flex items-center gap-1.5 px-5 py-2.5 rounded-full text-white text-sm font-bold"
                    style={{ background: 'linear-gradient(135deg,#FF007F,#FFD700)' }}
                  >
                    <Play className="w-4 h-4" fill="white" /> Play
                  </button>
                )}
                <button
                  onClick={() => navigate(`/genre/${genreSlug}`)}
                  data-testid="sleek-aotw-hub"
                  className="px-4 py-2.5 rounded-full border border-white/15 text-white/80 text-sm font-semibold hover:bg-white/5"
                >
                  Open Hub
                </button>
                <button
                  onClick={shareSpotlight}
                  disabled={sharing}
                  data-testid="sleek-aotw-share"
                  className="ml-auto flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-[#FFD700]/40 text-[#FFD700] text-sm font-semibold hover:bg-[#FFD700]/10 disabled:opacity-50"
                >
                  {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ) : (
            <div className="h-32 rounded-2xl border border-white/10 flex flex-col items-center justify-center text-center px-6" data-testid="sleek-aotw-empty">
              <p className="text-white/80 text-sm font-semibold">No spotlight on this station yet</p>
              <p className="text-white/40 text-xs mt-1">Upload tracks to a genre to crown its Artist of the Week.</p>
            </div>
          )}

          {/* Genre Hubs */}
          <div className="mt-10 mb-3 flex items-center gap-2">
            <Radio className="w-5 h-5 text-[#00F0FF]" />
            <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Stations</h2>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {genres.filter(g => g.id !== 'all').map(g => (
              <button
                key={g.id}
                onClick={() => navigate(`/genre/${g.id}`)}
                data-testid={`sleek-hub-${g.id}`}
                className="group relative overflow-hidden rounded-2xl p-4 text-left border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition-colors"
              >
                <div
                  className="absolute -right-6 -top-6 w-20 h-20 rounded-full blur-2xl opacity-40 group-hover:opacity-70 transition-opacity"
                  style={{ background: g.color }}
                />
                <span className="relative block text-white font-bold text-base leading-tight">{g.name}</span>
                <span className="relative mt-1 flex items-center gap-1 text-xs font-semibold" style={{ color: g.color }}>
                  Enter <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </button>
            ))}
          </div>

          {/* New Releases */}
          {hub?.new_releases?.length > 0 && (
            <>
              <div className="mt-10 mb-3 flex items-center gap-2">
                <Sparkles className="w-5 h-5" style={{ color: accent }} />
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  New on {hub.label}
                </h2>
              </div>
              <div className="flex gap-3 overflow-x-auto scrollbar-hide -mx-5 px-5 pb-2">
                {hub.new_releases.slice(0, 12).map((t, i) => (
                  <button
                    key={t.id || t.track_id || i}
                    onClick={() => playRoomTrack && playRoomTrack({ id: t.artist_id, tracks: [t] })}
                    data-testid={`sleek-release-${t.id || t.track_id || i}`}
                    className="flex-shrink-0 w-32 text-left"
                  >
                    <div className="w-32 h-32 rounded-xl overflow-hidden bg-white/10 relative group">
                      {t.artwork ? (
                        <img src={t.artwork} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white/30 text-2xl font-black">♪</div>
                      )}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                        <Play className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="white" />
                      </div>
                    </div>
                    <p className="mt-2 text-white text-sm font-semibold truncate">{t.track_title || 'Untitled'}</p>
                    <p className="text-white/40 text-xs truncate">{t.artist_name || ''}</p>
                  </button>
                ))}
              </div>
            </>
          )}
        </section>
      </div>

      {/* Vote sheet */}
      {showVote && (
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setShowVote(false)}
          data-testid="sleek-vote-modal"
        >
          <div className="w-full max-w-md bg-[#12101A] border border-white/10 rounded-2xl p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-white font-semibold text-sm">Rate this track</h4>
              <button onClick={() => setShowVote(false)} className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            {hasTrack ? (
              <VotingBar trackId={currentTrack.id} initialVoteCounts={currentTrack.vote_counts} initialUserVote={null} />
            ) : (
              <p className="text-white/60 text-sm text-center py-6">Play a track to vote on it.</p>
            )}
          </div>
        </div>
      )}

      {showPromo && (
        <PromoKitModal open={showPromo} type="track" id={currentTrack?.id} onClose={() => setShowPromo(false)} />
      )}
    </div>
  );
};

export default SleekHome;
